import { ApiFilled, ReloadOutlined } from "@ant-design/icons";
import {
  Cancellation,
  DOCS_URLS,
  Tenant,
  useAuthFetch,
  useHasRole,
} from "@p0security/frontend-core";
import * as firestoreProvider from "@p0security/frontend-core/react";
import { ErrorDisplay } from "@p0security/frontend-core/react";
import type { GrafanaIrmIntegrationDoc } from "@p0security/integrations-grafana-irm-shared";
import { SINGLETON_ITEM } from "@p0security/shared/install/constants";
import {
  Alert,
  Button,
  Form,
  Input,
  Select,
  Space,
  Spin,
  Typography,
} from "@p0security/ui";
import { GraphTooltip } from "components/GraphTooltip";
import * as firestore from "firebase/firestore";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { IntegrationCard } from "../IntegrationCard";
import { OwnerAlert } from "../OwnerAlert";
import { GrafanaIrmIconUrl } from "../constants.tsx";
import { grafanaIrmTeamLabel } from "./labels.ts";
import type { GrafanaIrmTeam } from "./labels.ts";

type FormValues = { serviceAccountToken: string; stackUrl: string };

const InstallForm: React.FC<{
  onInstall: (values: FormValues) => Promise<void>;
  isInstalling: boolean;
}> = ({ onInstall, isInstalling }) => {
  const [form] = Form.useForm<FormValues>();
  return (
    <Form form={form} onFinish={onInstall} layout="vertical">
      <Form.Item
        label="Stack URL"
        name="stackUrl"
        rules={[
          {
            required: true,
            message: "Your Grafana Cloud stack URL is required",
          },
        ]}
      >
        <Input placeholder="https://acme.grafana.net" />
      </Form.Item>
      <Form.Item
        label="Service account token"
        name="serviceAccountToken"
        rules={[
          {
            required: true,
            message: "A Grafana service account token is required",
          },
        ]}
      >
        <Input.Password placeholder="glsa_…" />
      </Form.Item>
      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          loading={isInstalling}
          icon={<ApiFilled />}
        >
          Install integration
        </Button>
      </Form.Item>
    </Form>
  );
};

const InstallPrompt: React.FC<{ onShowForm: () => void }> = ({
  onShowForm,
}) => (
  <>
    <Typography.Paragraph>
      In your Grafana Cloud stack, create a service account (Administration &gt;
      Users and access &gt; Service accounts) with:
    </Typography.Paragraph>
    <ul>
      <li>Basic role Viewer</li>
      <li>IRM role OnCaller</li>
    </ul>
    <Typography.Paragraph>
      Then add a token to it. Full instructions are in the{" "}
      <a href={DOCS_URLS.GRAFANA_IRM_INSTALL}>P0 docs</a>.
    </Typography.Paragraph>
    <Button icon={<ApiFilled />} onClick={onShowForm} type="primary">
      Install integration
    </Button>
  </>
);

/** Selects teams that cause auto-approval, and syncs them to Firestore. */
const GrafanaIrmTeamSelect: React.FC<{
  integration: firestoreProvider.KnownFirestoreDoc<GrafanaIrmIntegrationDoc>;
}> = ({ integration }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<any>();
  const authFetch = useAuthFetch(setError);
  const canEdit = useHasRole("owner");

  const onRefresh = useCallback(
    async (cancellation?: Cancellation) => {
      if (!canEdit) return;
      setIsRefreshing(true);
      try {
        await authFetch("integrations/grafana-irm/refresh", {
          method: "POST",
          cancellation,
        });
      } finally {
        setIsRefreshing(false);
      }
    },
    [authFetch, canEdit]
  );

  const { docs: teams } =
    firestoreProvider.useFirestoreCollection<GrafanaIrmTeam>(
      `integrations/grafana-irm/teams`,
      { live: true, tenantAware: true }
    );

  const options = useMemo(
    () =>
      teams?.map((t) => (
        <Select.Option key={t.data.id} label={t.data.name}>
          {grafanaIrmTeamLabel(t.data)}
        </Select.Option>
      )),
    [teams]
  );

  const currentlyWrittenIds = integration.doc?.data.selectedTeamIds ?? [];

  const onUpdate = useMemo(
    () => async (selected: string[]) => {
      setIsUpdating(true);
      try {
        await firestore.updateDoc(integration.ref, {
          selectedTeamIds: selected,
        } as GrafanaIrmIntegrationDoc);
      } finally {
        setIsUpdating(false);
      }
    },
    [integration.ref]
  );

  useEffect(() => {
    const cancellation = new Cancellation();
    onRefresh(cancellation);
    return cancellation.cancel;
  }, [onRefresh]);

  const onClickRefresh = useCallback(() => onRefresh(), [onRefresh]);

  return error ? (
    <ErrorDisplay title="Could not fetch Grafana IRM teams" error={error} />
  ) : (
    <Space direction="vertical" size="middle">
      <div>
        Select teams for auto-approval:
        <div className="flex">
          {isUpdating || isRefreshing || options === undefined ? (
            <Spin />
          ) : (
            <>
              <Select
                mode="multiple"
                allowClear
                disabled={!canEdit}
                className="grow"
                placeholder="None selected"
                defaultValue={currentlyWrittenIds}
                onChange={onUpdate}
                optionFilterProp="label"
              >
                {options}
              </Select>
              {canEdit && (
                <GraphTooltip title="Fetch latest teams from Grafana IRM">
                  <Button onClick={onClickRefresh}>
                    <ReloadOutlined />
                  </Button>
                </GraphTooltip>
              )}
            </>
          )}
        </div>
        <Typography.Text type="secondary">
          On call for a team means on call in the first step of its
          direct-paging escalation chain.
        </Typography.Text>
      </div>
    </Space>
  );
};

export const GrafanaIrm: React.FC<object> = () => {
  const tenantId = useContext(Tenant);
  const integrationData =
    firestoreProvider.useFirestoreDoc<GrafanaIrmIntegrationDoc>(
      `o/${tenantId}/integrations/grafana-irm`,
      { live: true }
    );
  const [isInstalling, setIsInstalling] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [showForm, setShowForm] = useState(false);
  const authFetch = useAuthFetch(setError);
  const canInstall = useHasRole("owner");
  const handleShowForm = useCallback(() => setShowForm(true), []);

  const handleInstall = useCallback(
    async ({ serviceAccountToken, stackUrl }: FormValues) => {
      setIsInstalling(true);
      setError(undefined);
      const response = await authFetch("integrations/grafana-irm/config", {
        method: "POST",
        json: {
          root: {
            _: {
              serviceAccountToken: { clearText: serviceAccountToken },
              stackUrl,
            },
          },
        },
      });
      setIsInstalling(false);
      if (response) setShowForm(false);
    },
    [authFetch]
  );

  const isInstalled = useMemo(
    () =>
      integrationData.doc?.data.root?.[SINGLETON_ITEM]?.state === "installed",
    [integrationData.doc?.data.root]
  );

  if (integrationData.loading) return <Spin />;

  return (
    <IntegrationCard
      integration="grafana-irm"
      logo={GrafanaIrmIconUrl}
      title="Grafana IRM"
      integrationDoc={integrationData.doc}
    >
      {error && <Alert type="error" message={error} />}
      {isInstalled && integrationData.doc ? (
        <Space direction="vertical" size="middle">
          <Typography.Text>
            This integration has been installed and is ready for use.
          </Typography.Text>
          <GrafanaIrmTeamSelect integration={integrationData} />
        </Space>
      ) : showForm ? (
        <InstallForm onInstall={handleInstall} isInstalling={isInstalling} />
      ) : canInstall ? (
        <InstallPrompt onShowForm={handleShowForm} />
      ) : (
        <OwnerAlert />
      )}
    </IntegrationCard>
  );
};
